import React, { useState, useMemo, useCallback, useEffect, type ReactElement } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import { Box, Text, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import PageIcon from '@atlaskit/icon/core/page';

import {
	CommentType,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { OPTIMISTIC_SAVE_COMMENT_ID } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { formatTimestampForAriaLabel } from '@confluence/comments-util/entry-points/dateUtils.ts';
import { useGetCommentStatus } from '@confluence/comments-util/entry-points/commentStatus.ts';
import { useCommentsPanelState } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import type {
	CommentLocation,
	GeneralCommentLocation,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';

import {
	type CommentThreadMouseEnterParams,
	handleCommentThreadMouseEnter,
	handleCommentThreadMouseLeave,
} from '../helpers/threadHelpers.ts';
import { getShouldShowRepliesSection } from '../helpers/repliesHelpers.ts';

// TODO COMMENTS-5941: Use Pick from CommentRepliesProps after moving CommentReplies component into comments-common
export type CommentComponentProps = {
	comment: CommentData | ReplyData;
	pageInfo: PageInfoNode | null;
	pageMode: PageMode;
	isUnread: boolean;
	isHovered: boolean;
	threadKey: string;
	hideBranchingStyle: boolean;
	canAddComments: boolean;
	isThreadHovered: boolean;
};

// TODO COMMENTS-5941: Use Pick from CommentProps after moving Comment component into comments-common
export type CommentRepliesComponentProps = {
	parentComment: CommentData;
	pageInfo: PageInfoNode | null;
	hoveredCommentId: string;
	pageMode: PageMode;
	parentCommentId: string;
	handleMouseEnter: (id?: string) => void;
	handleMouseLeave: () => void;
	canAddComments: boolean;
	isEditorExpanded: boolean;
};

export type BaseCommentThreadProps = {
	commentThreadRef?: React.RefObject<HTMLDivElement>;
	commentComponent: (commentComponentProps: CommentComponentProps) => ReactElement;
	commentRepliesComponent: (
		commentRepliesComponentProps: CommentRepliesComponentProps,
	) => ReactElement;
	parentComment: CommentData & {
		isFirstPageComment?: boolean;
	};
	eventEmitter?: AnnotationUpdateEmitter;
	pageMode: PageMode;
	shouldRenderCommentTypeSeparator?: boolean;
	canAddComments: boolean;
	handleCommentThreadClick: (event: React.MouseEvent) => void;
	handleCommentThreadMouseDown: () => void;
	testIdPrefix?: string;
	isEditorExpanded: boolean;
	/** When true, always render the replies section (e.g. comment summary popup needs the Reply button even with 0 replies) */
	forceShowRepliesSection?: boolean;
};

const styles = cssMap({
	threadBoxStyles: {
		cursor: 'pointer',
		listStyle: 'none',
		position: 'relative',
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'), // TODO COMMENTS-5910: override to space.250 for ObjectCommentThread
		paddingTop: token('space.200'), // TODO COMMENTS-5910: override to space.100 for ObjectCommentThread
		paddingBottom: token('space.200'),
		margin: '0',
		display: 'flex',
		flexDirection: 'column',
		// TODO COMMENTS-5910: Add &:focus and &:focus-visible stiles for ObjectCommentThread
	},
	selectedThreadStyles: {
		backgroundColor: token('color.background.input.hovered'),
	},
	commentTypeSeperatorStyle: {
		gap: token('space.050'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`, // TODO COMMENTS-5910: no borderTop for ObjectCommentThread
	},
	commentBodyBoxStyles: {
		listStyle: 'none',
		position: 'relative',
		// TODO COMMENTS-5910: override marginRight: token('space.100') for ObjectCommentThread
	},
});

const i18n = defineMessages({
	commentTypeSeperatorText: {
		id: 'comments-common.comment.thread.type-seperator',
		defaultMessage: 'General comments',
		description: 'Text for the seperator of inline and general comment section',
	},
	threadContentAriaLabelWithName: {
		// COMMENTS-5979: ObjectCommentThread doesn't have timestamp, but it probably should
		id: 'comments-common.thread.content.aria-label.with-name',
		defaultMessage: '{name}’s comment thread, {timestamp}',
		description:
			'Aria-label for content associated with the comment thread, with the first comment author name and timestamp',
	},
});

const noop = () => {
	// This is intentional
};

// This component renders the parent comment and its replies.
// It also handles UI states, like hover, pressed, etc. and checks add comments permissions
export const BaseCommentThread = ({
	commentThreadRef,
	commentComponent,
	commentRepliesComponent,
	parentComment,
	eventEmitter,
	pageMode,
	shouldRenderCommentTypeSeparator,
	canAddComments,
	handleCommentThreadClick,
	handleCommentThreadMouseDown: onCommentThreadMouseDown,
	testIdPrefix,
	isEditorExpanded,
	forceShowRepliesSection = false,
}: BaseCommentThreadProps) => {
	const intl = useIntl();
	const { formatMessage } = intl;

	const { currentlySelectedThreadKey, isCurrentlySelectedThreadOptimisticallyAdded } =
		useCommentsPanelState();
	const { pageInfo } = usePageInfo();
	const { unreadCommentIDSet } = useUnreadCommentsState();

	const threadKey =
		parentComment.type === CommentType.INLINE
			? ((parentComment.location as CommentLocation).inlineMarkerRef ?? '')
			: parentComment.id;

	const [isThreadHovered, setIsThreadHovered] = useState<boolean>(false);
	const [isThreadPressed, setIsThreadPressed] = useState<boolean>(false);
	const [hoveredCommentId, setHoveredCommentId] = useState<string | undefined>(undefined);
	const [commentIsFocused, setCommentIsFocused] = useState<boolean>(false);

	const isCurrentSelectedComment =
		currentlySelectedThreadKey === threadKey ||
		// Special handling to avoid UI flicker when optimistically adding a footer comment.
		// See comments in `next/packages/comments-panel-utils/src/useCommentsPanel.ts` for details.
		(parentComment.type === CommentType.GENERAL &&
			!!isCurrentlySelectedThreadOptimisticallyAdded &&
			threadKey === OPTIMISTIC_SAVE_COMMENT_ID);

	useEffect(() => {
		const parentIdOfSelectedReply = parentComment?.replies?.find(
			(reply) => reply.id === currentlySelectedThreadKey,
		);
		if (isCurrentSelectedComment || parentIdOfSelectedReply) {
			setIsThreadPressed(true);
		} else {
			setIsThreadPressed(false);
		}
	}, [currentlySelectedThreadKey, isCurrentSelectedComment, parentComment?.replies]);

	const handleCommentThreadMouseDown = useCallback(() => {
		onCommentThreadMouseDown?.();

		/**
		 * Limiting executions of this function because updating isThreadPressed
		 * rerenders every component in this tree. Most notably, a user clicking into
		 * the reply comment editor will call this function every time (and really any sibling component).
		 * 1. isThreadPressed is only used as a style variable alongside isCurrentSelectedComment
		 * 2. if clicking doesn't change threadKey then currentlySelectedThreadKey never changes either
		 * which are the two variables isCurrentSelectedComment compares. We should only call this function
		 * if threadKey has changed.
		 */
		if (!isCurrentSelectedComment) {
			setIsThreadPressed(true);
		}
	}, [onCommentThreadMouseDown, isCurrentSelectedComment]);

	const handleCommentThreadHoverProps: CommentThreadMouseEnterParams = useMemo(
		() => ({
			threadKey,
			setIsThreadHovered,
			setHoveredCommentId,
			pageMode,
			eventEmitter,
		}),
		[threadKey, setIsThreadHovered, setHoveredCommentId, pageMode, eventEmitter],
	);

	const handleMouseEnter = useCallback(
		(id?: string) => {
			handleCommentThreadMouseEnter({
				...handleCommentThreadHoverProps,
				id,
			});
		},
		[handleCommentThreadHoverProps],
	);

	const handleMouseLeave = useCallback(() => {
		handleCommentThreadMouseLeave(handleCommentThreadHoverProps);
	}, [handleCommentThreadHoverProps]);

	const handleFocus = useCallback(
		(event: React.FocusEvent) => {
			event.stopPropagation();
			setCommentIsFocused(true);
		},
		[setCommentIsFocused],
	);

	const handleBlur = useCallback(
		(event: React.FocusEvent) => {
			// Only clear focus if we're not focusing a child element
			if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as Node)) {
				setCommentIsFocused(false);
			}
		},
		[setCommentIsFocused],
	);

	const resolveProperties =
		(parentComment.location as CommentLocation)?.inlineResolveProperties ||
		(parentComment.location as GeneralCommentLocation)?.commentResolveProperties ||
		undefined;

	const commentStatus = useGetCommentStatus({
		resolveProperties,
	});

	const timestamp = formatTimestampForAriaLabel({
		intl,
		dateString: parentComment.createdAtNonLocalized,
		commentStatus,
		resolveProperties,
	});

	const testIdPrefixWithDelimiter = testIdPrefix ? `${testIdPrefix}-` : '';

	return (
		<>
			{shouldRenderCommentTypeSeparator && (
				<Flex
					xcss={styles.commentTypeSeperatorStyle}
					as="li"
					testId="comment-section-separator"
					alignItems="center"
				>
					<PageIcon
						testId="general-comment-page-icon"
						color={token('color.icon.subtlest')}
						label={formatMessage(i18n.commentTypeSeperatorText)}
					/>
					<Text color="color.text.subtlest" weight="medium">
						{formatMessage(i18n.commentTypeSeperatorText)}
					</Text>
				</Flex>
			)}
			<Box
				ref={commentThreadRef}
				id={`${testIdPrefixWithDelimiter}comment-thread-${threadKey}-container`}
				testId={`${testIdPrefixWithDelimiter}comment-thread-${threadKey}-container`}
				xcss={cx(
					styles.threadBoxStyles,
					(isCurrentSelectedComment || isThreadPressed) && styles.selectedThreadStyles,
				)}
				data-selected-comment-thread={
					isCurrentSelectedComment && (parentComment.location as CommentLocation)?.inlineMarkerRef
						? (parentComment.location as CommentLocation).inlineMarkerRef
						: undefined
				}
				onClick={handleCommentThreadClick}
				onMouseDown={handleCommentThreadMouseDown}
				onMouseUp={() => setIsThreadPressed(false)}
				onFocus={
					expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
						? undefined
						: handleFocus
				}
				onBlur={
					expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
						? undefined
						: handleBlur
				}
				onKeyDown={() => {}} // TODO COMMENTS-5943: Implement actual onKeyDown handler
				tabIndex={0}
				aria-label={formatMessage(i18n.threadContentAriaLabelWithName, {
					name: parentComment.author?.displayName || formatMessage(commentsi18n.anonymous),
					timestamp,
				})}
				role="button"
			>
				<Box
					testId={`${testIdPrefixWithDelimiter}comment-thread-${parentComment.id}-parent`}
					xcss={styles.commentBodyBoxStyles}
					onMouseEnter={
						expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
							? undefined
							: () => handleMouseEnter(parentComment.id)
					}
					onMouseLeave={
						expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
							? undefined
							: handleMouseLeave
					}
				>
					{commentComponent({
						comment: parentComment,
						pageInfo,
						pageMode,
						isUnread: unreadCommentIDSet.has(parentComment.id),
						isHovered: expValEquals(
							'confluence_frontend_fix_comment_actions_visibility',
							'isEnabled',
							true,
						)
							? false
							: hoveredCommentId === parentComment.id ||
								(commentIsFocused && fg('confluence_comments_hover_state_on_focused_comment')),
						threadKey,
						hideBranchingStyle: expValEquals('confluence_comments_tuneup', 'isEnabled', true)
							? parentComment.replies.length === 0 && !isEditorExpanded
							: (!parentComment.isOpen || !canAddComments) && parentComment.replies.length === 0,
						canAddComments,
						isThreadHovered,
					})}
				</Box>
				{(getShouldShowRepliesSection({
					replies: parentComment.replies,
					isEditorExpanded,
				}) ||
					forceShowRepliesSection) &&
					commentRepliesComponent({
						parentComment,
						pageInfo,
						hoveredCommentId: hoveredCommentId ?? '',
						pageMode,
						parentCommentId: parentComment.id,
						handleMouseEnter: expValEquals(
							'confluence_frontend_fix_comment_actions_visibility',
							'isEnabled',
							true,
						)
							? noop
							: handleMouseEnter,
						handleMouseLeave: expValEquals(
							'confluence_frontend_fix_comment_actions_visibility',
							'isEnabled',
							true,
						)
							? noop
							: handleMouseLeave,
						canAddComments,
						isEditorExpanded,
					})}
			</Box>
		</>
	);
};
